import { thousandSeparated } from '../../../../helpers/formatting';
import type {
  GetDisplayValuesFromFilterValues,
  GetFiltersByName,
  GetFiltersFromSearchFilters,
  GetFilterValuesFromSearchFilters,
  GetMotorDisplayValuesFromFilterValues,
  GetMotorFiltersFromSearchFilters,
  Mapper,
  MotorOptionMapper,
} from '../../../../components/HomePage/components/MotorSearch/MotorSearch.helpers.typed';
import {
  DisplayWithValue,
  ListItem,
} from '../../../../components/HomePage/components/MotorSearch/MotorSearch.typed';
import type {
  GetSearchFilterMakeModelItem,
  IGetSearchFilterMakeModel,
} from '../../../../types';

const formatValue: Mapper = (value) => ({
  label: value.displayName,
  value: value.value.toString(),
});

const formatMotorOption: MotorOptionMapper = (value, index) => {
  return {
    id: index,
    ...value,
  };
};

const formatPrice: Mapper = (value) => ({
  label: `\u20AC${thousandSeparated(value.displayName)}`,
  value: value.value.toString(),
});

const getDisplayValuesFromFilterValues: GetDisplayValuesFromFilterValues = (
  filterValues = [],
  mapper = formatValue,
) => {
  const placeholder = {
    label: filterValues[0]?.displayName || '',
    value: 'placeholder',
  };
  const filterValuesOnly = filterValues.slice(1).map(mapper);
  return [placeholder, ...filterValuesOnly];
};

const getMotorDisplayValuesFromFilterValues: GetMotorDisplayValuesFromFilterValues =
  (filterValues = [], mapper) => {
    return filterValues.map(mapper);
  };
const getFiltersByName: GetFiltersByName = (searchFilters = [], name) =>
  searchFilters.find((filter) => filter.name === name);

const getFilterValuesFromSearchFilters: GetFilterValuesFromSearchFilters = (
  searchFilters = [],
  filterName,
  filterType,
) => {
  const filters = getFiltersByName(searchFilters, filterName);
  return filters?.subFilters?.[0]?.values?.[0][filterType] || [];
};

const replacePlaceholderLabelWith = (
  options: DisplayWithValue[],
  label: string,
) => {
  return options.map((option) => {
    if (option.value === 'placeholder') {
      return {
        ...option,
        label,
      };
    }
    return option;
  });
};

const getYearOptions: GetFiltersFromSearchFilters = (searchFilters) => {
  const yearFiltersFrom = getFilterValuesFromSearchFilters(
    searchFilters,
    'year',
    'from',
  );
  const yearFiltersTo = getFilterValuesFromSearchFilters(
    searchFilters,
    'year',
    'to',
  );

  return [
    replacePlaceholderLabelWith(
      getDisplayValuesFromFilterValues(yearFiltersFrom, formatValue),
      'Min Year',
    ),
    replacePlaceholderLabelWith(
      getDisplayValuesFromFilterValues(yearFiltersTo, formatValue),
      'Max Year',
    ),
  ];
};

const getPriceOptions: GetFiltersFromSearchFilters = (searchFilters) => {
  const priceFiltersFrom = getFilterValuesFromSearchFilters(
    searchFilters,
    'price',
    'from',
  );
  const priceFiltersTo = getFilterValuesFromSearchFilters(
    searchFilters,
    'price',
    'to',
  );
  return [
    replacePlaceholderLabelWith(
      getDisplayValuesFromFilterValues(priceFiltersFrom, formatPrice),
      'Min Price',
    ),
    replacePlaceholderLabelWith(
      getDisplayValuesFromFilterValues(priceFiltersTo, formatPrice),
      'Max Price',
    ),
  ];
};

const getMakesOptions: GetMotorFiltersFromSearchFilters = (searchFilters) => {
  const makesOptions = getFilterValuesFromSearchFilters(
    searchFilters,
    'makeModel',
    'all',
  );
  const makesOptionValues = makesOptions.filter(
    ({ displayName }) => displayName !== 'Any',
  );

  return getMotorDisplayValuesFromFilterValues(
    makesOptionValues,
    formatMotorOption,
  );
};

const getPopularMakesOptions: GetMotorFiltersFromSearchFilters = (
  searchFilters,
) => {
  const makesOptions = getFilterValuesFromSearchFilters(
    searchFilters,
    'makeModel',
    'popular',
  );
  const makesOptionValues = makesOptions.filter(
    ({ displayName }) => displayName !== 'Any',
  );
  return getMotorDisplayValuesFromFilterValues(
    makesOptionValues,
    formatMotorOption,
  );
};

const filterModels = (modelsData?: GetSearchFilterMakeModelItem[]) =>
  (modelsData ?? []).filter(
    (item: GetSearchFilterMakeModelItem) => item.displayName !== 'Any',
  );

const getModelOptions = (modelData: IGetSearchFilterMakeModel): ListItem[] => {
  const { all, popular } = modelData;
  const allModels = filterModels(all);
  const popularModels = filterModels(popular);

  if (popularModels.length > 0) {
    return [
      { displayName: 'All Models', value: '' },
      { displayName: 'Popular', value: '' },
      ...popularModels,
      { displayName: 'Models list', value: '' },
      ...allModels,
    ];
  } else {
    return [
      { displayName: 'All Models', value: '' },
      { displayName: 'Models list', value: '' },
      ...allModels,
    ];
  }
};

export {
  getPriceOptions,
  getYearOptions,
  getMakesOptions,
  getPopularMakesOptions,
  getModelOptions,
};
